/**
 * Общий шаг «Реквизиты организации» для мастеров заказов (F2-11/FX-E — унификация шага-1
 * СОУТ/профриска с паттерном обучения): по умолчанию — компакт-карточка read-only с кнопкой
 * «Изменить» в заголовке; по клику открывается `CompanyProfileForm` (чёрный ящик — её саму
 * не трогаем, перекомпоновка полей — отдельная задача FX-B). Сохранение обновляет и профиль
 * компании, и снимок ТЕКУЩЕГО заказа без модерации — конкретный API-вызов передаёт вызывающий
 * визард через `onSave` (СОУТ/профриски/обучение — `ordersApi.patchRequisites`), сам компонент
 * про заказы ничего не знает. Для уже поданного (readOnly) заказа — полный read-view
 * `CompanyProfileView` без кнопки «Изменить» (реквизиты зафиксированы в снимке).
 */
import { useState } from 'react'
import type { CompanyProfile, DaDataPartyMatch, UpdateCompanyProfileInput } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import type { ExtraRequisiteRequirement } from '@/lib/validation'
import { CompanyProfileForm } from '@/company/company-profile-form'
import { CompanyProfileView } from '@/company/company-profile-view'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'

function CompanyCompactView({ org, onEdit }: { org: CompanyProfile; onEdit: () => void }) {
  return (
    <Card
      header={
        <div className="flex items-center justify-between gap-2">
          <span>Данные компании</span>
          <Button size="sm" variant="secondary" onClick={onEdit}>
            Изменить
          </Button>
        </div>
      }
    >
      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 nw:grid-cols-2">
        <div>
          <dt className="text-12 text-txt-tertiary">Полное наименование</dt>
          <dd className="text-txt">{org.nameFull || '—'}</dd>
        </div>
        <div>
          <dt className="text-12 text-txt-tertiary">ИНН / КПП</dt>
          <dd className="text-txt">
            {org.inn || '—'} / {org.kpp || '—'}
          </dd>
        </div>
        <div>
          <dt className="text-12 text-txt-tertiary">Юридический адрес</dt>
          <dd className="text-txt">{org.legalAddress || '—'}</dd>
        </div>
        <div>
          <dt className="text-12 text-txt-tertiary">Руководитель</dt>
          <dd className="text-txt">
            {org.directorName || '—'}
            {org.directorPosition ? ` · ${org.directorPosition}` : ''}
          </dd>
        </div>
      </dl>
    </Card>
  )
}

/**
 * Полны ли реквизиты для КОМПАКТНОГО отображения на первом шаге мастера заказа
 * (centrika-docs!79 «Начальный вариант в мастере заказа»). Компакт-карточка показывается только
 * когда заполнены все входящие в неё данные; иначе сразу открывается форма редактирования.
 * Значение из одних пробелов считается незаполненным. КПП обязателен только для организации с
 * ИНН из 10 цифр (юрлицо); у ИП (ИНН 12 цифр) КПП нет. Поля руководителя не учитываются, когда
 * они скрыты (`hideManagerFields`) — их нельзя заполнить, поэтому они не должны держать форму
 * открытой.
 */
export function requisitesCompleteForCompactView(
  org: CompanyProfile,
  opts?: { hideManagerFields?: boolean },
): boolean {
  const filled = (v?: string | null) => Boolean((v ?? '').trim())
  if (!filled(org.nameFull)) return false
  if (!filled(org.inn)) return false
  if ((org.inn ?? '').trim().length === 10 && !filled(org.kpp)) return false
  if (!filled(org.legalAddress)) return false
  if (!opts?.hideManagerFields) {
    if (!filled(org.directorName)) return false
    if (!filled(org.directorPosition)) return false
  }
  return true
}

export interface CompanyRequisitesStepProps {
  /** Снимок реквизитов заказа/профиля; `undefined` — профиль ещё не заведён. */
  organization?: CompanyProfile
  /** Заказ уже подан (`!canEdit`) — полный read-only просмотр снимка, без «Изменить». */
  readOnly: boolean
  /** Текст подсказки для read-only просмотра поданного заказа (см. `CompanyProfileView.hint`). */
  readOnlyHint?: string
  /** Выполняет PATCH реквизитов (профиль + снимок заказа) и возвращает свежий профиль. */
  onSave: (input: UpdateCompanyProfileInput) => Promise<CompanyProfile>
  /** Вызывается после успешного сохранения — до возврата в компакт-режим. */
  onSaved?: () => void
  /** Открыть форму сразу в режиме редактирования (напр., переход «Изменить» из шага «Проверка»). */
  initialEditing?: boolean
  hideManagerFields?: boolean
  requireExtraRequisites?: ExtraRequisiteRequirement
  onDaDataLookup?: (inn: string) => Promise<DaDataPartyMatch[]>
}

export function CompanyRequisitesStep({
  organization,
  readOnly,
  readOnlyHint,
  onSave,
  onSaved,
  initialEditing = false,
  hideManagerFields,
  requireExtraRequisites,
  onDaDataLookup,
}: CompanyRequisitesStepProps) {
  // Начальный вариант (centrika-docs!79): в мастере заказа компакт-карточка — только при полных
  // реквизитах; если хотя бы одно поле не заполнено, шаг сразу открывается формой редактирования
  // (как по кнопке «Изменить»). Явный `initialEditing` (напр. переход «Изменить» из «Проверки»)
  // имеет приоритет. Полное отсутствие профиля (`!organization`) обрабатывается ниже отдельно.
  const [editing, setEditing] = useState(
    initialEditing ||
      (organization ? !requisitesCompleteForCompactView(organization, { hideManagerFields }) : false),
  )
  const [error, setError] = useState<string | null>(null)

  async function handleSave(input: UpdateCompanyProfileInput): Promise<CompanyProfile> {
    setError(null)
    try {
      const next = await onSave(input)
      onSaved?.()
      setEditing(false)
      return next
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить реквизиты'))
      throw err
    }
  }

  if (!organization) {
    return (
      <Alert variant="danger">
        Реквизиты компании не найдены — заполните профиль в разделе «Настройки».
      </Alert>
    )
  }

  if (readOnly) {
    return <CompanyProfileView profile={organization} hint={readOnlyHint} />
  }

  return (
    <div className="flex flex-col gap-3">
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {editing ? (
        <CompanyProfileForm
          profile={organization}
          onSave={handleSave}
          hideManagerFields={hideManagerFields}
          requireExtraRequisites={requireExtraRequisites}
          onDaDataLookup={onDaDataLookup}
        />
      ) : (
        <CompanyCompactView org={organization} onEdit={() => setEditing(true)} />
      )}
    </div>
  )
}
